import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import InterestBalanceRow from './InterestBalanceRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ interestBalance }) =>
  interestBalance.map(interest => (
    <InterestBalanceRow key={interest.id} {...interest} />
  ));

Tbody.propTypes = {
  interestBalance: PropTypes.array.isRequired
};

const InterestBalanceTable = ({
  interestBalance,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell variant="head" key="client" align="left">
          {t('COMMON.CLIENT')}
        </TableCell>
        <TableCell variant="head" key="equipment" align="left">
          {t('COMMON.EQUIPMENT')}
        </TableCell>
        <TableCell variant="head" key="period" align="center">
          {t('BILLING_VIEW.PERIOD')}
        </TableCell>
        <TableCell variant="head" key="initialBalance" align="center">
          {t('INTEREST_BALANCE.INITIAL_BALANCE')}
        </TableCell>
        <TableCell variant="head" key="subscription" align="center">
          {t('INTEREST_BALANCE.CONTRACT_DISCHARGE')}
        </TableCell>
        <TableCell variant="head" key="interest" align="center">
          {t('INTEREST_BALANCE.INTEREST')}
        </TableCell>
        <TableCell variant="head" key="interestChange" align="center">
          {t('INTEREST_BALANCE.INTEREST_CHANGE')}
        </TableCell>
        <TableCell
          variant="head"
          key="finalBalance"
          align="center"
          sx={{ fontWeight: 'bold' }}
        >
          {t('INTEREST_BALANCE.FINAL_BALANCE')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(
    () => () => <Tbody interestBalance={interestBalance} />,
    [interestBalance]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) =>
          `${from}-${to} ${t('COMMON.OF')} ${c}`
        }
      />
    ),
    [page, count, handleChangePage, t]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

InterestBalanceTable.propTypes = {
  interestBalance: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

InterestBalanceTable.defaultProps = {
  count: 0,
  loading: false
};

export default InterestBalanceTable;
